import './location-selector.css';

import EventEmitter from "eventemitter3";
import { Modal } from '../modal';
import { Data } from "../../../models/data";
import { EventTypes } from '../../../core/event-bus';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { Util } from '../../../utils/util';
import { FieldType } from '../../../types/enums';
import { ILocationSelector } from './location-selector.interface';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { GeolocationService } from '../../../services/geolocation-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { InputValidator } from '../../../validators/input-validator';
import { ValidationSubmitGuard } from '../../shared/Validation/validation-submit-guard';

/**
 * Represents the Location Selector modal for prompting desired country and zip code
 */
export class LocationSelector extends Modal {
    private _data: Data;
    private _geolocationService: GeolocationService;
    private _item: ILocationSelector;
    private _bindingHelper: TwoWayBindingHelper<ILocationSelector>;
    private _resolve: ((data: any) => void) | null = null;

    /**
     * Initializes a new instance of the Location Selector Modal
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Data} data - The service managing application data.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, geolocationService: GeolocationService, dataLayer: DataLayerService) {
        super("modal_location_selector", eventBus, context, dataLayer, localizationService, { hasCloseButton: false, ariaLabel: "Location Selector" });
        this._data = data;
        this._geolocationService = geolocationService;
        this._item = this._initialModel();
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._eventBusListeners();
    }

    /**
     * Gets a true or false value indicating whether the selected country is the Bahamas.
     */
    get isBahamas(): boolean {
        return this._data.getBrandProperty("Countries").find(x => x.CountryID === Number(this._item.countryId))?.IsoCode === 'BH';
    }

    private _initialModel(): ILocationSelector {
        return {
            countryId: '1',
            zip: ''
        };
    }

    private readonly _eventBusListeners = (): void => {
        this._eventBus.on(EventTypes.BRAND_DATA_RETRIEVED, async () => {
            this._item = this._createModel();
            this._bindingHelper = new TwoWayBindingHelper(this._item); //need to rebind after this._item is replaced again
            this._setTwoWayListeners();
            this._populateCountryField();
        });
    }

    /**
     * Creates the initial location selector model.
     * @returns {ILocationSelector} The initial model containing the default country and zip code.}
     */
    private _createModel(): ILocationSelector {
        return {
            countryId: this._data.getBrandProperty("Countries").find(x => x.CountryID === this._data.getProperty("StoreInfo").countryId)?.CountryID.toString() || '1',
            zip: ''
        };
    }

    /**
     * Initializes the location selector modal by setting up event listeners and data bindings.
     */
    public override init(): void {
        super.init();
        this._hideErrorElements();
        this._setEventListeners();
        this._setTwoWayListeners();
    }

    /**
     * Opens the location selector modal and sets up necessary elements.
     */
    public override open(): void {
        super.open();
        Util.ensureAnnouncerPresent();
    }

    /**
     * Populates the country dropdown with available countries.
     */
    private _populateCountryField(): void {
        const ddlCountry = document.getElementById("ddl_country") as HTMLSelectElement;
        let optionsHtml = '';
        for (const country of this._data.getBrandProperty("Countries")) {
            optionsHtml += `
                <option value="${country.CountryID}">${country.Name}</option>
            `;
        }
        ddlCountry.innerHTML = optionsHtml;
    }

    /**
     * Sets up event listeners for the location selector modal.
     */
    protected _setEventListeners(): void {
        document.getElementById("txt_zip")?.addEventListener("input", this._sanitizeZipInput);
        Util.setElement("onchange", "ddl_country", this._handleOnCountryChange)

        const buttonOk = document.getElementById("btn_location_selector_ok")
        buttonOk?.addEventListener("click", async () => await this._handleOk());

        const buttonClose = document.getElementById("btn_location_selector_close")
        buttonClose?.addEventListener("click", this._handleClose);

        this._element?.addEventListener("cancel", this._handleClose);
    }

    /**
     * Sets up two-way data binding for form inputs.
     */
    private _setTwoWayListeners(): void {
        this._bindingHelper.bindToInput("countryId", "ddl_country");
        this._bindingHelper.bindToInput("zip", "txt_zip");
    }

    /**
     * Handles oninput validation for the zip code field.
     * @param event
     */
    private _sanitizeZipInput = (event: Event): void => {
        const target = event.target as HTMLInputElement;
        if (target) {
            target.value = target.value.replace(/\D/g, ''); // Remove non-numeric characters
            if (target.value.length > 10) {
                target.value = target.value.slice(0, 10);
            }
        }
    }

    /**
     * Handles the change event for the country dropdown.
     */
    private _handleOnCountryChange = (): void => {
        if (this.isBahamas)
            Util.hideElement("div_zip_field_container");
        else
            Util.showElement("div_zip_field_container");
    }

    /**
     * Handles the OK button click event for the location selector modal.
     * @returns
     */
    private readonly _handleOk = async (): Promise<void> => {
        if (!(await this._guardSubmitValidation())) {
            return;
        }
        if (this._resolve) {
            this._resolve({ countryId: this._item.countryId, zip: this._item.zip });
        } else {
            this._eventBus.emit(EventTypes.LOCATION_SELECTION, this._item);
        }
        this.close();
    }

    private readonly _guardSubmitValidation = async (): Promise<boolean> => {
        return await ValidationSubmitGuard.validateAndAnnounce({
            container: this._element ?? this._elementId,
            validate: async () => {
                const isValid = await this._validateLocationSelectorInputs();
                return isValid || this.isBahamas;
            },
            scrollToFirstInvalid: false
        });
    }

    /**
     * Handles the close button click event for the location selector modal.
     */
    private readonly _handleClose = (): void => {
        this.close();
        if (this._resolve) {
            this._resolve(null);
            return;
        }
        this._eventBus.emit(EventTypes.LOCATION_SELECTION);
    }

    /**
     * Hides error elements for the location selector modal.
     */
    private _hideErrorElements(): void {
        Util.hideElementErrors("txt_zip", "spn_zip_error");
    }

    /**
     * Validates the location selector inputs.
     * Ensures that the zip code is valid.
     * 
     *@returns {Promise<boolean>} - A promise resolving to `true` if validation passes, otherwise `false`.
     */
    private async _validateLocationSelectorInputs(): Promise<boolean> {
        this._hideErrorElements();

        const validationFields = [
            { type: FieldType.ZIP, inputId: "txt_zip", errorId: "spn_zip_error" }
        ]

        const results = await Promise.all(validationFields.map(async (field) => {
            return await InputValidator.validateAsync(this._geolocationService, field.type, field.inputId, field.errorId,
            undefined, undefined, this._localizationService, this._dataLayer)
        }));

        return !results.some(result => result);
    }

    public openDialog() {
        return new Promise<ILocationSelector | null>((resolve) => {
            this._resolve = resolve;
            this.open();
        });
    }
}
